'use client'

import * as React from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUserStore } from '@/lib/store'
import { useSyncSavedListingIds } from '@/components/listings'
import { useAuth } from '@/components/auth'
import { navPillCls, navPillBadgeNeutralCls, navPillLabelCls } from './nav-pill'
import { useI18n } from '@/lib/i18n'

// Header Liked-flats shortcut.
// - Signed out → the heart prompts sign-in (opens the auth dialog) instead of
//   routing to an empty page, matching the save-heart on cards.
// - Signed in → routes to /liked and shows a badge mirroring the real, server-
//   synced saved count (useSyncSavedListingIds hydrates it once per uid; the
//   store is localStorage-backed so the number is stable across reloads).
export function LikedButton() {
  const router = useRouter()
  const pathname = usePathname()
  const { t } = useI18n()
  const { user, signIn } = useAuth()
  const { hydrated } = useSyncSavedListingIds()
  // Preferred source: the live-liked count /liked publishes (delisted
  // excluded, app/liked/page.tsx:231) — it is authoritative and persisted, so
  // the badge agrees with that page (user requirement 2026-07-15).
  //
  // But it is null until /liked has been opened ONCE, which meant a freshly
  // signed-in user saw NO badge until they clicked the heart (user report
  // 2026-08-04). So fall back to the server-synced saved-id count, which
  // useSyncSavedListingIds hydrates per-uid right after sign-in. Same
  // precedent as the notifications page (app/notifications/page.tsx:101).
  // The fallback can transiently over-count if a liked listing was delisted;
  // it self-corrects the moment /liked publishes the real number.
  const activeCount = useUserStore((s) => s.likedActiveCount)
  const savedIds = useUserStore((s) => s.savedListingIds)
  const count = user && hydrated ? (activeCount ?? savedIds.length) : 0
  const active = pathname.startsWith('/liked')

  const label =
    count > 0 ? `${t('header.likedFlats', 'Liked flats')} (${count})` : t('header.likedFlats', 'Liked flats')

  // Rendered as a design navBtn pill (shell.jsx Header nav group): label +
  // inline count badge, icons-only below xl.
  return (
    <button
      type="button"
      onClick={() => (user ? router.push('/liked') : signIn())}
      aria-label={label}
      title={label}
      className={cn(navPillCls(active), 'outline-none')}
    >
      <Heart className={cn('h-[18px] w-[18px]', active && 'fill-current')} />
      <span className={navPillLabelCls}>{t('header.liked', 'Liked')}</span>
      {count > 0 && <span className={navPillBadgeNeutralCls}>{count > 99 ? '99+' : count}</span>}
    </button>
  )
}
